import { z } from 'zod';

/**
 * DevRobot project manifest (`devrobot.json`), schema version 1.
 *
 * The manifest is executable project configuration: it declares what the
 * application needs. It never carries secret values and it can never grant
 * permissions (BRD FR-MAN-01). Server-side authorization ignores it entirely.
 */

export const MANIFEST_FILENAME = 'devrobot.json';
export const CURRENT_SCHEMA_VERSION = 1;
export const SUPPORTED_SCHEMA_VERSIONS = [1] as const;

/** 3-50 chars, lowercase letters, digits and dashes; starts with a letter. */
export const PROJECT_KEY_RE = /^[a-z][a-z0-9-]{1,48}[a-z0-9]$/;
export const ENV_NAME_RE = /^[a-z][a-z0-9-]{0,30}$/;
export const VAR_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
export const SERVICE_NAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9_.-]*$/;
export const FILE_NAME_RE = /^[a-z0-9][a-z0-9._-]{0,63}$/;
export const HOSTNAME_RE = /^(?=.{1,253}$)(?!-)([a-z0-9-]{1,63}\.)+[a-z]{2,63}$/;

const serviceName = z.string().regex(SERVICE_NAME_RE, 'must be a valid Compose service name');
const port = z.number().int().min(1).max(65535);

const installStepSchema = z.object({
  name: z.string().min(1).max(40),
  command: z.string().min(1),
  cwd: z.string().default('.'),
  env: z.record(z.string(), z.string()).optional(),
});

const processSchema = installStepSchema.extend({ port: port.optional() });

const developmentSchema = z.object({
  composeFiles: z.array(z.string().min(1)).optional(),
  dependencies: z.array(serviceName).default([]),
  /**
   * Commands that install what local development needs, run by `devrobot dev install`.
   * Absent: detected from the lockfiles. `[]`: nothing to install, detection is skipped.
   * Install steps never receive configuration values: a fresh clone installs without a session.
   */
  install: z.array(installStepSchema).optional(),
  processes: z.array(processSchema).default([]),
  shutdown: z
    .object({
      stopDependencies: z.boolean().default(true),
      timeoutSeconds: z.number().int().min(1).max(300).default(10),
    })
    .default({ stopDependencies: true, timeoutSeconds: 10 }),
});

const buildSchema = z.object({
  service: serviceName,
  context: z.string().default('.'),
  dockerfile: z.string().default('Dockerfile'),
  target: z.string().optional(),
  buildArgs: z.array(z.string().regex(VAR_NAME_RE)).default([]),
});

const containerSchema = z.object({
  composeFiles: z.array(z.string().min(1)).min(1),
  profiles: z.array(z.string()).default([]),
  builds: z.array(buildSchema).default([]),
  platforms: z.array(z.string().regex(/^linux\/(amd64|arm64|arm\/v7)$/)).default(['linux/amd64']),
  appService: serviceName,
  /**
   * Setup services that run once and exit with code 0 (seeders, init jobs). Services another
   * service waits for with `condition: service_completed_successfully` are detected without it.
   */
  runOnce: z.array(serviceName).default([]),
});

const exposeSchema = z.object({
  service: serviceName,
  containerPort: port,
  name: z.string().min(1).max(40).optional(),
  protocol: z.enum(['http', 'tcp']).default('http'),
});

const variableSchema = z.object({
  name: z.string().regex(VAR_NAME_RE, 'must be a valid environment variable name'),
  description: z.string().max(500).optional(),
  required: z.boolean().default(false),
  secret: z.boolean().default(false),
  default: z.string().optional(),
  services: z.array(serviceName).optional(),
  environments: z.array(z.string().regex(ENV_NAME_RE)).optional(),
  buildTime: z.boolean().default(false),
});

const fileSchema = z.object({
  name: z.string().regex(FILE_NAME_RE, 'must be lowercase letters, digits, dot, dash or underscore'),
  description: z.string().max(500).optional(),
  required: z.boolean().default(true),
  destination: z.string().regex(/^\/[^\0]*$/, 'must be an absolute path inside the container'),
  services: z.array(serviceName).optional(),
  mode: z.string().regex(/^0[0-7]{3}$/).default('0400'),
  uid: z.number().int().min(0).optional(),
  gid: z.number().int().min(0).optional(),
  environments: z.array(z.string().regex(ENV_NAME_RE)).optional(),
});

const healthSchema = z.object({
  service: serviceName,
  type: z.enum(['http', 'tcp', 'docker']).default('http'),
  path: z.string().startsWith('/').default('/'),
  port: port.optional(),
  timeoutSeconds: z.number().int().min(5).max(900).default(60),
  intervalSeconds: z.number().int().min(1).max(60).default(2),
});

export const DATABASE_TYPES = ['postgres', 'mysql', 'mariadb', 'mongodb', 'redis', 'other'] as const;
/** Database types with a supported backup/restore adapter (FR-BKP-01). */
export const BACKUP_ADAPTERS = ['postgres'] as const;

const databaseSchema = z.object({
  service: serviceName,
  type: z.enum(DATABASE_TYPES),
  volume: z.string().min(1),
  user: z.string().default('postgres'),
  database: z.string().optional(),
  credential: z
    .object({
      variable: z.string().regex(VAR_NAME_RE),
      services: z.array(serviceName).min(1),
    })
    .optional(),
  backup: z.boolean().default(true),
});

const persistenceSchema = z.object({
  volumes: z
    .array(z.object({ name: z.string().min(1), service: serviceName, description: z.string().optional() }))
    .default([]),
  databases: z.array(databaseSchema).default([]),
});

const lifecycleSchema = z.object({
  migrations: z
    .object({
      service: serviceName,
      command: z.array(z.string().min(1)).min(1),
    })
    .optional(),
});

export const manifestSchema = z
  .object({
    $schema: z.string().optional(),
    schemaVersion: z.literal(1),
    key: z.string().regex(PROJECT_KEY_RE, 'must be 3-50 lowercase letters, digits or dashes, starting with a letter'),
    name: z.string().trim().min(1).max(100),
    description: z.string().max(500).optional(),
    development: developmentSchema.default({
      dependencies: [],
      processes: [],
      shutdown: { stopDependencies: true, timeoutSeconds: 10 },
    }),
    container: containerSchema,
    expose: z.array(exposeSchema).default([]),
    config: z
      .object({
        variables: z.array(variableSchema).default([]),
        files: z.array(fileSchema).default([]),
      })
      .default({ variables: [], files: [] }),
    health: healthSchema.optional(),
    persistence: persistenceSchema.default({ volumes: [], databases: [] }),
    lifecycle: lifecycleSchema.default({}),
    stop: z.object({ keepRunning: z.array(serviceName).default([]) }).default({ keepRunning: [] }),
  })
  .superRefine((m, ctx) => {
    const seenVars = new Set<string>();
    m.config.variables.forEach((v, i) => {
      if (seenVars.has(v.name)) {
        ctx.addIssue({ code: 'custom', path: ['config', 'variables', i, 'name'], message: `duplicate variable ${v.name}` });
      }
      seenVars.add(v.name);
      if (v.secret && v.default !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['config', 'variables', i, 'default'],
          message: 'secret variables cannot declare a default value in the manifest',
        });
      }
      if (v.secret && v.buildTime) {
        ctx.addIssue({
          code: 'custom',
          path: ['config', 'variables', i, 'buildTime'],
          message: 'build-time variables are passed to GitLab CI and cannot be secrets',
        });
      }
    });
    const seenFiles = new Set<string>();
    m.config.files.forEach((f, i) => {
      if (seenFiles.has(f.name)) {
        ctx.addIssue({ code: 'custom', path: ['config', 'files', i, 'name'], message: `duplicate private file ${f.name}` });
      }
      seenFiles.add(f.name);
    });
    const volumeNames = new Set(m.persistence.volumes.map((v) => v.name));
    m.persistence.databases.forEach((d, i) => {
      if (!volumeNames.has(d.volume)) {
        ctx.addIssue({
          code: 'custom',
          path: ['persistence', 'databases', i, 'volume'],
          message: `volume ${d.volume} must be declared in persistence.volumes`,
        });
      }
    });
    const seenBuilds = new Set<string>();
    m.container.builds.forEach((b, i) => {
      if (seenBuilds.has(b.service)) {
        ctx.addIssue({ code: 'custom', path: ['container', 'builds', i, 'service'], message: `duplicate build for ${b.service}` });
      }
      seenBuilds.add(b.service);
      for (const arg of b.buildArgs) {
        const decl = m.config.variables.find((v) => v.name === arg);
        if (decl?.secret) {
          ctx.addIssue({
            code: 'custom',
            path: ['container', 'builds', i, 'buildArgs'],
            message: `build argument ${arg} is declared secret; runtime secrets are never passed to CI builds`,
          });
        }
      }
    });
    const longRunning = new Set([m.container.appService, ...(m.health ? [m.health.service] : []), ...m.expose.map((e) => e.service)]);
    m.container.runOnce.forEach((s, i) => {
      if (longRunning.has(s)) {
        ctx.addIssue({
          code: 'custom',
          path: ['container', 'runOnce', i],
          message: `${s} is the app service, the health-check service or exposed, so it cannot be a run-once setup service`,
        });
      }
    });
    const seenExpose = new Set<string>();
    m.expose.forEach((e, i) => {
      const id = `${e.service}:${e.containerPort}`;
      if (seenExpose.has(id)) {
        ctx.addIssue({ code: 'custom', path: ['expose', i], message: `duplicate exposure ${id}` });
      }
      seenExpose.add(id);
    });
    if (m.health && m.health.type !== 'docker' && m.health.port === undefined) {
      const exposed = m.expose.find((e) => e.service === m.health!.service);
      if (!exposed) {
        ctx.addIssue({
          code: 'custom',
          path: ['health', 'port'],
          message: 'health.port is required when the health service is not listed in expose',
        });
      }
    }
  });

export type Manifest = z.output<typeof manifestSchema>;
export type ManifestInput = z.input<typeof manifestSchema>;
export type ManifestVariable = Manifest['config']['variables'][number];
export type ManifestInstallStep = z.output<typeof installStepSchema>;
export type ManifestProcess = Manifest['development']['processes'][number];
export type ManifestFile = Manifest['config']['files'][number];
export type ManifestDatabase = Manifest['persistence']['databases'][number];
export type ManifestHealth = NonNullable<Manifest['health']>;
export type ManifestExpose = Manifest['expose'][number];

export interface ManifestIssue {
  path: string;
  message: string;
}

export type ManifestValidation =
  | { ok: true; manifest: Manifest; warnings: ManifestIssue[] }
  | { ok: false; errors: ManifestIssue[]; unsupportedVersion?: unknown };

/** Validates a parsed manifest. Unsupported schema versions get an actionable compatibility message. */
export function validateManifest(raw: unknown): ManifestValidation {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { ok: false, errors: [{ path: '', message: 'manifest must be a JSON object' }] };
  }
  const version = (raw as Record<string, unknown>).schemaVersion;
  if (version === undefined) {
    return { ok: false, errors: [{ path: 'schemaVersion', message: 'schemaVersion is required (current version is 1)' }] };
  }
  if (!SUPPORTED_SCHEMA_VERSIONS.includes(version as 1)) {
    return {
      ok: false,
      unsupportedVersion: version,
      errors: [
        {
          path: 'schemaVersion',
          message:
            typeof version === 'number' && version > CURRENT_SCHEMA_VERSION
              ? `manifest schemaVersion ${version} is newer than this DevRobot release supports (max ${CURRENT_SCHEMA_VERSION}). Upgrade the devrobot CLI and platform.`
              : `manifest schemaVersion ${String(version)} is not supported. Supported versions: ${SUPPORTED_SCHEMA_VERSIONS.join(', ')}.`,
        },
      ],
    };
  }
  const parsed = manifestSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.map((i) => ({ path: i.path.map(String).join('.'), message: i.message })),
    };
  }
  return { ok: true, manifest: parsed.data, warnings: manifestWarnings(parsed.data) };
}

/** Parses JSON text and validates it. */
export function parseManifest(text: string): ManifestValidation {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err) {
    return { ok: false, errors: [{ path: '', message: `invalid JSON: ${(err as Error).message}` }] };
  }
  return validateManifest(raw);
}

function manifestWarnings(m: Manifest): ManifestIssue[] {
  const warnings: ManifestIssue[] = [];
  const suspicious = /(PASSWORD|SECRET|TOKEN|PRIVATE|API_KEY|CREDENTIAL)/i;
  m.config.variables.forEach((v, i) => {
    // Classification is explicit; the name is only used to raise a hint, never to decide.
    if (!v.secret && suspicious.test(v.name)) {
      warnings.push({
        path: `config.variables.${i}`,
        message: `${v.name} looks sensitive but is not marked secret; set "secret": true if it holds a credential`,
      });
    }
  });
  if (!m.health) {
    warnings.push({ path: 'health', message: 'no health check declared; deployments will only verify that containers are running' });
  }
  return warnings;
}

/** Services that receive a variable: explicit list, otherwise the application service only. */
export function variableServices(m: Manifest, v: ManifestVariable): string[] {
  return v.services && v.services.length > 0 ? v.services : [m.container.appService];
}

export function fileServices(m: Manifest, f: ManifestFile): string[] {
  return f.services && f.services.length > 0 ? f.services : [m.container.appService];
}

export function appliesToEnvironment(item: { environments?: string[] }, environment: string): boolean {
  return !item.environments || item.environments.length === 0 || item.environments.includes(environment);
}

/** A minimal, valid manifest used by `devrobot init` as a starting point. */
export function manifestSkeleton(key: string, name: string, composeFile: string, appService: string): ManifestInput {
  return {
    $schema: 'https://devrobot.dev/schema/devrobot.v1.json',
    schemaVersion: 1,
    key,
    name,
    development: { dependencies: [], processes: [] },
    container: { composeFiles: [composeFile], builds: [{ service: appService }], appService },
    expose: [],
    config: { variables: [], files: [] },
    persistence: { volumes: [], databases: [] },
  };
}
